import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Divider,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Heading,
  Link,
  Stack,
  Text,
  UseDisclosureReturn,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Show, useSuccess } from '@pelicargo/ui';
import { prettyDate } from '@pelicargo/utils';
import { useForm } from 'react-hook-form';
import { HiArrowTopRightOnSquare } from 'react-icons/hi2';
import { z } from 'zod';

import { trpc } from '../../config/trpc';
import { useAirportTimeZone } from '../../hooks/airport/useAirportTimeZone';
import { useEarliestExpiringRate } from '../../hooks/import/useEarliestExpiringRate';
import { BaseDrawer } from '../BaseDrawer';
import { LabeledText } from '../LabeledText';
import { SimpleDatePicker } from '../SimpleDatePicker';
import { SimpleForm } from '../SimpleForm';

const schema = z.object({
  expires_at: z.date(),
});

type FormValues = z.infer<typeof schema>;

type Props = {
  isOpen: UseDisclosureReturn['isOpen'];
  onClose: UseDisclosureReturn['onClose'];
  rateImportToExtend: any;
};

export const ExtendRateDrawer = ({ isOpen, onClose, rateImportToExtend }: Props) => {
  const { handleSuccess } = useSuccess();
  const toast = useToast();
  const methods = useForm({
    resolver: zodResolver(schema),
  });
  const [earliestExpiredRate] = useEarliestExpiringRate(rateImportToExtend?.id as number);
  const { data: originAirport } = useAirportTimeZone(earliestExpiredRate?.origin_airport_id);

  const { mutateAsync: extendImport, isLoading } = trpc.extendImport.useMutation();

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  const handleExpirySubmit = async (data: FormValues) => {
    try {
      if (!rateImportToExtend?.id) return;

      await extendImport({ ...data, import_id: rateImportToExtend?.id });
      handleSuccess('Successfully extended import and rate expiries');
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSubmit = methods.handleSubmit(handleExpirySubmit);

  return (
    <BaseDrawer onClose={handleClose} isOpen={isOpen}>
      <DrawerHeader>
        <Heading variant="h4">Extend Import Expiry</Heading>
      </DrawerHeader>
      <DrawerBody>
        <SimpleForm {...methods} submitOnBlur={false}>
          <form onSubmit={handleSubmit}>
            <Alert status="warning" mb="5">
              <AlertIcon />
              <Stack spacing={0}>
                <AlertTitle>This is irreversible.</AlertTitle>
                <AlertDescription fontSize="sm">
                  This will expire {rateImportToExtend?.name} ({rateImportToExtend?.id}) and update existing rates with
                  the updated expiry.
                </AlertDescription>
              </Stack>
            </Alert>
            <Stack spacing={6}>
              <LabeledText label="Name" value={rateImportToExtend?.name} />
              <LabeledText
                label="Earliest Expiry"
                value={prettyDate(earliestExpiredRate?.expires_at as any, {
                  timeZone: originAirport?.time_zone,
                })}
              />
              <LabeledText label="Provider" value={rateImportToExtend?.provider?.name} />
              <Divider />
              <SimpleDatePicker<FormValues>
                name="expires_at"
                label="Expiry Date"
                variant="filled"
                showTime={false}
                minValue={earliestExpiredRate?.expires_at as any}
                defaultValue={earliestExpiredRate?.expires_at as any}
                isRequired
                matchWidth
                showClearButton
              />
            </Stack>
          </form>
        </SimpleForm>
      </DrawerBody>
      <DrawerFooter>
        <Stack
          direction="row"
          justify={rateImportToExtend?.id ? 'space-between' : 'right'}
          align="center"
          w="full"
          spacing="4"
        >
          <Show if={!!rateImportToExtend?.id}>
            <Text>ID: {rateImportToExtend?.id}</Text>
          </Show>

          <Stack direction="row" align="center" justify="flex-end" spacing="4">
            <Button
              as={Link}
              href={rateImportToExtend?.source || ''}
              isExternal
              variant="link"
              size="sm"
              target="_blank"
              rightIcon={<HiArrowTopRightOnSquare />}
            >
              Open Link to Sheet
            </Button>
            <Button colorScheme="primary" isLoading={isLoading} onClick={handleSubmit}>
              Extend
            </Button>
          </Stack>
        </Stack>
      </DrawerFooter>
    </BaseDrawer>
  );
};
